import React from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { useAuthFormState, useQueryParams } from '#states/hooks.js';
import { Alert, AppleIcon, Button, FloatingInput, GoogleIcon } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';
import { Link } from '#views/Router.jsx';

export function LoginPage() {
  const authFormState = useAuthFormState();
  const query = useQueryParams();
  const passwordResetComplete = query.passwordReset === '1';

  // Read signals directly in render for automatic reactivity
  const email = authFormState.email;
  const password = authFormState.password;
  const isLoading = authFormState.isLoading;
  const error = authFormState.error;

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await authFormState.signInWithEmail();
  };

  const footerContent = (
    <p>
      Got an invite?{' '}
      <Link
        to={ROUTES.SIGNUP}
        className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
      >
        Create your account
      </Link>
      , otherwise{' '}
      <a
        href="https://elara.travel/apply"
        className="text-accent hover:underline bg-transparent border-none p-0 m-0 cursor-pointer"
      >
        join the waitlist
      </a>
      .
    </p>
  );

  return (
    <SessionLayout title="Welcome back" footer={footerContent}>
      <form onSubmit={handleEmailSignIn} className="space-y-6">
        {passwordResetComplete && <Alert>Your password has been reset. Please sign in with your new password.</Alert>}
        {error && <Alert variant="destructive">{error}</Alert>}
        <FloatingInput
          id="signin-email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => authFormState.setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
        <div className="space-y-1">
          <FloatingInput
            id="signin-password"
            label="Password"
            type="password"
            value={password}
            onChange={(e) => authFormState.setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <p className="text-xs mt-2">
            <Link to={ROUTES.FORGOT_PASSWORD} className="link">
              Forgot your password?
            </Link>
          </p>
        </div>
        <div className="flex flex-col">
          <Button type="submit" size="lg" disabled={isLoading}>
            Sign in
          </Button>
        </div>
      </form>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-muted" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-background-secondary px-2 text-muted-foreground">or</span>
        </div>
      </div>

      <div className="flex justify-center gap-4">
        <Button
          variant="icon-outline"
          size="icon-lg"
          className="rounded"
          onClick={async () => authFormState.signInWithGoogle()}
          disabled={isLoading}
          tooltip="Sign in with Google"
        >
          <GoogleIcon />
        </Button>
        {authFormState.isAppleSignInAvailable && (
          <Button
            variant="icon-outline"
            size="icon-lg"
            className="rounded"
            onClick={async () => authFormState.signInWithApple()}
            disabled={isLoading}
            tooltip="Sign in with Apple"
          >
            <AppleIcon />
          </Button>
        )}
      </div>
    </SessionLayout>
  );
}
